<section class="page">
  <header class="page-header">
    <div>
      <h1>Send offer / campaign</h1>
      <p>Send push, email, or both to a single user, specific users, everyone, or a filtered audience</p>
    </div>
    <div class="header-actions">
      <a routerLink="/notifications" class="btn btn-secondary">Back to list</a>
    </div>
  </header>

  @if (error()) {
    <div class="alert alert-error">{{ error() }}</div>
  }
  @if (success()) {
    <div class="alert alert-success">{{ success() }}</div>
  }

  <div class="panel create-panel">
    <form [formGroup]="form" class="create-form" (ngSubmit)="send()">
      <div class="audience-head full">
        <h3>Channel</h3>
        <div class="mode-tabs" role="tablist" aria-label="Delivery channel">
          <button
            type="button"
            class="mode-tab"
            [class.active]="channel() === 'push'"
            (click)="setChannel('push')"
          >
            Push
          </button>
          <button
            type="button"
            class="mode-tab"
            [class.active]="channel() === 'email'"
            (click)="setChannel('email')"
          >
            Email
          </button>
          <button
            type="button"
            class="mode-tab"
            [class.active]="channel() === 'both'"
            (click)="setChannel('both')"
          >
            Both
          </button>
        </div>
      </div>

      <label>
        <span>Type</span>
        <select formControlName="type">
          <option value="promo">promo (offers)</option>
          <option value="general">general</option>
          <option value="membership">membership</option>
        </select>
      </label>
      <label>
        <span>Sample limit</span>
        <input type="number" formControlName="sample_limit" min="1" max="100" />
      </label>

      @if (wantsPush()) {
        <div class="section-label full">Push content</div>
        <label>
          <span>Push title</span>
          <input type="text" formControlName="title" placeholder="Pay rent early" />
        </label>
        <label>
          <span>Data screen (optional)</span>
          <input type="text" formControlName="screen" placeholder="rent" />
        </label>
        <label class="full">
          <span>Push body</span>
          <textarea formControlName="body" rows="3" placeholder="Earn extra rewards this month."></textarea>
        </label>
      }

      @if (wantsEmail()) {
        <div class="section-label full">Email content</div>
        <label class="full">
          <span>Email subject</span>
          <input type="text" formControlName="email_subject" placeholder="Pay rent early and earn more" />
        </label>
        <label class="full">
          <span>Email body</span>
          <textarea
            formControlName="email_body"
            rows="4"
            placeholder="Earn extra Rentwize rewards when you pay rent early this month."
          ></textarea>
        </label>
        <p class="full muted audience-note">
          Sent as a branded Rentwize HTML email. Plain text only — line breaks are preserved.
        </p>
      }

      <div class="audience-head full">
        <h3>Audience</h3>
        <div class="mode-tabs" role="tablist" aria-label="Audience mode">
          <button
            type="button"
            class="mode-tab"
            [class.active]="audienceMode() === 'single'"
            (click)="setAudienceMode('single')"
          >
            Single user
          </button>
          <button
            type="button"
            class="mode-tab"
            [class.active]="audienceMode() === 'specific'"
            (click)="setAudienceMode('specific')"
          >
            Specific users
          </button>
          <button
            type="button"
            class="mode-tab"
            [class.active]="audienceMode() === 'all'"
            (click)="setAudienceMode('all')"
          >
            All users
          </button>
          <button
            type="button"
            class="mode-tab"
            [class.active]="audienceMode() === 'filters'"
            (click)="setAudienceMode('filters')"
          >
            Filters
          </button>
        </div>
      </div>

      @if (audienceMode() === 'single') {
        <div class="user-picker full">
          <span class="picker-label">User</span>

          @if (selectedUser(); as selected) {
            <div class="selected-chip">
              <div>
                <div class="chip-name">{{ userLabel(selected) }}</div>
                <div class="chip-email">{{ selected.email || 'No email' }}</div>
              </div>
              <button type="button" class="chip-remove" (click)="clearSingleUser()" aria-label="Clear user">
                ×
              </button>
            </div>
          } @else {
            <input
              type="search"
              class="picker-search"
              [value]="userSearch()"
              (input)="onSearchInput($event)"
              (focus)="openPicker()"
              placeholder="Search by name or email…"
              autocomplete="off"
            />
          }

          @if (pickerOpen() && !selectedUser()) {
            <div class="picker-dropdown">
              @if (loadingUsers()) {
                <div class="picker-empty">Loading users…</div>
              } @else if (!filteredUsers().length) {
                <div class="picker-empty">No users found</div>
              } @else {
                @for (user of filteredUsers(); track user.id) {
                  <button type="button" class="picker-option" (click)="selectSingleUser(user)">
                    <span class="opt-name">{{ userLabel(user) }}</span>
                    <span class="opt-email">{{ user.email || 'No email' }}</span>
                  </button>
                }
              }
            </div>
          }
        </div>
      }

      @if (audienceMode() === 'specific') {
        <div class="user-picker full">
          <span class="picker-label">Users</span>

          @if (selectedUsers().length) {
            <div class="chip-list">
              @for (user of selectedUsers(); track user.id) {
                <div class="selected-chip compact">
                  <div>
                    <div class="chip-name">{{ userLabel(user) }}</div>
                    <div class="chip-email">{{ user.email || 'No email' }}</div>
                  </div>
                  <button
                    type="button"
                    class="chip-remove"
                    (click)="removeSpecificUser(user.id)"
                    aria-label="Remove user"
                  >
                    ×
                  </button>
                </div>
              }
            </div>
          }

          <input
            type="search"
            class="picker-search"
            [value]="userSearch()"
            (input)="onSearchInput($event)"
            (focus)="openPicker()"
            placeholder="Search and add users by name or email…"
            autocomplete="off"
          />

          @if (pickerOpen()) {
            <div class="picker-dropdown">
              @if (loadingUsers()) {
                <div class="picker-empty">Loading users…</div>
              } @else if (!filteredUsers().length) {
                <div class="picker-empty">No users found</div>
              } @else {
                @for (user of filteredUsers(); track user.id) {
                  <button type="button" class="picker-option" (click)="addSpecificUser(user)">
                    <span class="opt-name">{{ userLabel(user) }}</span>
                    <span class="opt-email">{{ user.email || 'No email' }}</span>
                  </button>
                }
              }
            </div>
          }
        </div>
      }

      @if (audienceMode() === 'all') {
        <p class="full muted audience-note">
          Sends to every user in the system. Preview first to confirm the match count.
        </p>
      }

      @if (audienceMode() === 'filters') {
        <p class="full muted audience-note">
          Leave as Any to skip a filter. Filters are AND-combined.
        </p>

        <label>
          <span>Device</span>
          <select formControlName="device">
            <option value="all">all</option>
            <option value="ios">ios</option>
            <option value="android">android</option>
          </select>
        </label>
        <label>
          <span>Verified</span>
          <select formControlName="is_verified">
            <option value="any">Any</option>
            <option value="true">Yes</option>
            <option value="false">No</option>
          </select>
        </label>
        <label>
          <span>KYC status</span>
          <select formControlName="kyc_status">
            <option value="any">Any</option>
            <option value="pending">pending</option>
            <option value="submitted">submitted</option>
            <option value="approved">approved</option>
            <option value="rejected">rejected</option>
          </select>
        </label>
        <label>
          <span>Has FCM token</span>
          <select formControlName="has_fcm_token">
            <option value="any">Any</option>
            <option value="true">Yes</option>
            <option value="false">No</option>
          </select>
        </label>
        <label>
          <span>Has email</span>
          <select formControlName="has_email">
            <option value="any">Any</option>
            <option value="true">Yes</option>
            <option value="false">No</option>
          </select>
        </label>
        <label>
          <span>Has cards</span>
          <select formControlName="has_cards">
            <option value="any">Any</option>
            <option value="true">Yes</option>
            <option value="false">No</option>
          </select>
        </label>
        <label>
          <span>Has transactions</span>
          <select formControlName="has_transactions">
            <option value="any">Any</option>
            <option value="true">Yes</option>
            <option value="false">No</option>
          </select>
        </label>
        <label>
          <span>Has rent payments</span>
          <select formControlName="has_rent_payments">
            <option value="any">Any</option>
            <option value="true">Yes</option>
            <option value="false">No</option>
          </select>
        </label>
        <label>
          <span>Has wallet</span>
          <select formControlName="has_wallet">
            <option value="any">Any</option>
            <option value="true">Yes</option>
            <option value="false">No</option>
          </select>
        </label>
        <label>
          <span>Has beneficiaries</span>
          <select formControlName="has_beneficiaries">
            <option value="any">Any</option>
            <option value="true">Yes</option>
            <option value="false">No</option>
          </select>
        </label>
      }

      <div class="actions">
        <button
          class="btn btn-secondary"
          type="button"
          (click)="previewAudience()"
          [disabled]="previewing() || sending()"
        >
          {{ previewing() ? 'Previewing…' : 'Preview audience' }}
        </button>
        <button class="btn btn-primary" type="submit" [disabled]="sending() || previewing()">
          {{ sending() ? 'Sending…' : 'Send notification' }}
        </button>
      </div>
    </form>
  </div>

  @if (preview(); as p) {
    <div class="panel preview-panel">
      <div class="preview-summary">
        <h2>Audience preview</h2>
        <div class="stats">
          <div>
            <div class="stat-value">{{ p.count }}</div>
            <div class="stat-label">Matched users</div>
          </div>
          <div>
            <div class="stat-value">{{ p.sample_with_token }}</div>
            <div class="stat-label">Sample with token</div>
          </div>
          <div>
            <div class="stat-value">{{ p.sample_with_email ?? 0 }}</div>
            <div class="stat-label">Sample with email</div>
          </div>
          <div>
            <div class="stat-value">{{ p.firebase_ready ? 'Yes' : 'No' }}</div>
            <div class="stat-label">Firebase ready</div>
          </div>
          <div>
            <div class="stat-value">{{ p.smtp_ready ? 'Yes' : 'No' }}</div>
            <div class="stat-label">SMTP ready</div>
          </div>
        </div>
      </div>

      <div class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Email</th>
              <th>Device</th>
              <th>KYC</th>
              <th>FCM</th>
              <th>Email OK</th>
            </tr>
          </thead>
          <tbody>
            @if (!sampleRows().length) {
              <tr><td colspan="7" class="muted">No sample users</td></tr>
            } @else {
              @for (user of sampleRows(); track user.id) {
                <tr>
                  <td>{{ user.id }}</td>
                  <td>{{ user.first_name || '' }} {{ user.last_name || '' }}</td>
                  <td>{{ user.email || '—' }}</td>
                  <td>{{ user.current_device || '—' }}</td>
                  <td>{{ user.kyc_status || '—' }}</td>
                  <td>{{ user.has_fcm_token ? 'Yes' : 'No' }}</td>
                  <td>{{ user.has_email ? 'Yes' : 'No' }}</td>
                </tr>
              }
            }
          </tbody>
        </table>
      </div>
    </div>
  }
</section>
